import { get } from "lodash-es";
import { useI18n } from "vue-i18n";
import { marked } from 'marked';

export function getLanguage() {
    return localStorage.getItem("locale")
        || process.env.VUE_APP_LANGUAGE
        || "pt-BR";
}

export function formatTextArray(texts: any[]): string {
    try {
        let div = "";
        texts.forEach((item: any) => {
            div += `<p>${item}</p>`;
        });
        return div;
    } catch (_) {
        return "";
    }
}

export function getTranslate(data: {
    text?: string | null;
    long_text?: boolean;
    i18n?: boolean;
    concat?: any;
}): any {
    const text = get(data, "text", null);
    const i18n = get(data, "i18n", true);
    const longText = get(data, "long_text", false);
    const concat = data.concat ?? {};

    if (i18n) {
        const { t, tm } = useI18n();

        if (!longText) {
            return t(text || "empty", { concat: concat }) ?? "";
        } else {
            return formatTextArray(Object(tm(text || "array_empty")));
        }
    }
}

export function htmlToText(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    const paragraphs = doc.body.querySelectorAll("p");

    if (paragraphs.length > 0) {
        return Array.from(paragraphs)
            .map((p) => p.textContent?.trim() || "")
            .join("<br/>");
    }

    doc.body.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));

    let text = doc.body.textContent || "";

    text = text.replace(/\n\s*\n/g, "\n").replace("\n", "<br/>").trim();

    return text;
}

export function paginateText(
    text?: string | null,
    params?: {
        width?: string;
        height?: number;
        initial_height?: number;
        font_size?: string;
        font_family?: string;
        font_weight?: string;
        line_height?: string;
        letter_spacing?: string;
        text_align?: string;
        text_transform?: string;
    }
): { page: number; width: string; height: number; text: string; }[] {
    if (!text) return [];

    const {
        width = "250px",
        height = 0,
        font_size = "16px",
        font_family = "Arial",
        font_weight = "normal",
        line_height = "normal",
        letter_spacing = "0px",
        text_align = "left",
        text_transform = "none",
        initial_height = 0
    } = params || {};

    const div = document.createElement("div");

    div.style.width = width;
    div.style.fontSize = font_size;
    div.style.fontFamily = font_family;
    div.style.fontWeight = font_weight;
    div.style.lineHeight = line_height;
    div.style.letterSpacing = letter_spacing;
    div.style.textAlign = text_align;
    div.style.textTransform = text_transform;
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordBreak = 'break-word';
    div.style.position = "absolute";
    div.style.zIndex = "-99";
    div.style.opacity = '0';
    div.style.top = '0';
    div.style.left = '0';
    document.body.appendChild(div);

    const PAGE_DEFAULT = {
        page: 0,
        width,
        height: 0,
        text: "",
    };

    let PAGE = 0;
    let PARAGRAPH_TEXT = "";
    let INITIAL_HEIGHT = initial_height;

    const pages: any[] = [{
        ...PAGE_DEFAULT,
        height: 0,
    }];

    const paragraphs = text
        .replaceAll("<br>", "\n")
        .replaceAll("<br/>", "\n")
        .split("\n");

    paragraphs.forEach((paragraph) => {
        const words = paragraph.split(" ");

        words.forEach((word, index) => {
            if (PAGE > 0) {
                INITIAL_HEIGHT = 0;
            }

            const wordFormat = index === 0 ? word : ` ${word}`;
            div.innerText += wordFormat;

            if (div.offsetHeight + INITIAL_HEIGHT < height) {
                PARAGRAPH_TEXT += wordFormat;
                pages[PAGE].height = div.offsetHeight;
            }
            else if (div.offsetHeight + INITIAL_HEIGHT > height) {
                pages[PAGE].text = PARAGRAPH_TEXT;
                PAGE += 1;
                pages[PAGE] = { ...PAGE_DEFAULT };
                pages[PAGE].page = PAGE;
                PARAGRAPH_TEXT = "";
                PARAGRAPH_TEXT += word;
                pages[PAGE].height = div.offsetHeight;
            } else {
                PARAGRAPH_TEXT += wordFormat;
                pages[PAGE].text = PARAGRAPH_TEXT;
                PAGE += 1;
                pages[PAGE] = { ...PAGE_DEFAULT };
                pages[PAGE].page = PAGE;
                PARAGRAPH_TEXT = "";
                pages[PAGE].height = div.offsetHeight;
            }

            div.innerText = PARAGRAPH_TEXT;
        });

        INITIAL_HEIGHT += 4;
        PARAGRAPH_TEXT += '\n';
    });

    if (PARAGRAPH_TEXT.length > 0)
        pages[PAGE].text = PARAGRAPH_TEXT;

    setTimeout(() => {
        document.body.removeChild(div);
    }, 100);

    return pages;
}

export function getParagraphHeights(
    text?: string | null,
    params?: {
        width?: string;
        font_size?: string;
        font_weight?: string;
        line_height?: string;
        letter_spacing?: string;
        text_transform?: string;
    }
): { text: string; height: number }[] {
    if (!text) return [];

    const {
        width = "250px",
        font_size = "16px",
        font_weight = "normal",
        line_height = "normal",
        letter_spacing = "0px",
        text_transform = "none",
    } = params || {};

    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.zIndex = "-999";
    container.style.opacity = "0";
    container.style.width = width;
    container.style.fontSize = font_size;
    container.style.fontWeight = font_weight;
    container.style.lineHeight = line_height;
    container.style.letterSpacing = letter_spacing;
    container.style.textTransform = text_transform;
    container.style.whiteSpace = "pre-wrap";
    container.style.visibility = "hidden";

    document.body.appendChild(container);

    const paragraphs = text.replaceAll("<br/>", "\n").split("\n");

    const results: { text: string; height: number }[] = [];

    for (const paragraph of paragraphs) {
        const p = document.createElement("div");
        p.innerText = paragraph.trim();
        container.appendChild(p);
        const height = p.offsetHeight;
        results.push({ text: paragraph.trim(), height });
        container.removeChild(p);
    }

    setTimeout(() => {
        document.body.removeChild(container);
    }, 100);
    return results;
}

export function getMarkdownBlockHeights(
    markdown?: string | null,
    params?: {
        width?: string;
        font_size?: string;
        font_weight?: string;
        line_height?: string;
        letter_spacing?: string;
        text_transform?: string;
    }
): { html: string; height: number }[] {
    if (!markdown) return [];

    const {
        width = "210mm",
        font_size = "16px",
        font_weight = "normal",
        line_height = "1.5",
        letter_spacing = "0px",
        text_transform = "none",
    } = params || {};

    const html = marked(markdown);
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.zIndex = "-999";
    container.style.opacity = "0";
    container.style.width = width;
    container.style.fontSize = font_size;
    container.style.fontWeight = font_weight;
    container.style.lineHeight = line_height;
    container.style.letterSpacing = letter_spacing;
    container.style.textTransform = text_transform;
    container.style.visibility = "hidden";

    // Inserir HTML completo renderizado
    container.innerHTML = html as any;
    document.body.appendChild(container);

    const results: { html: string; height: number }[] = [];

    for (const child of Array.from(container.children)) {
        const height = (child as HTMLElement).offsetHeight;
        results.push({ html: child.outerHTML, height });
    }

    document.body.removeChild(container);
    return results;
}

export function truncate(value: string | null, size = 100) {
    if (!value) return "";

    const length = value.trim().length;

    const formatted = value.substring(0, size);

    return length > size ? `${formatted}...` : formatted;
}

export function sanitizeText(text: string): string {
    if (!text) return text;

    return text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9\s]/g, "")
        .replace(/\s+/g, " ")
        .trim();
}